import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt, faBuilding, faChevronDown, faChevronRight, faSearch, faAddressBook, faShoppingCart, faFileLines } from "@fortawesome/free-solid-svg-icons";
import apiAgent from "../../../../app/api/apiAgent";
import { toast } from "react-toastify";

interface MapSearchResultsProps {
  properties: any[];
  onLocationClick?: (property: any) => void; // New prop for handling location clicks
  onOwnerDetailsClick?: (property: any) => void; // New prop for handling owner details clicks
  onPropertyReportClick?: (property: any) => void; // New prop for handling property report clicks
}

const MapSearchResults: React.FC<MapSearchResultsProps> = ({ properties, onLocationClick, onOwnerDetailsClick, onPropertyReportClick }) => {
  // Add error state
  const [error, setError] = useState<string | null>(null);
  
  // State to track which property groups are expanded - moved before conditional return
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  // State to store filtered properties
  const [filteredProperties, setFilteredProperties] = useState<any[]>([]);
  // State to store properties grouped by location
  const [locationGroups, setLocationGroups] = useState<Record<string, any[]>>({});
  // State for search term
  const [searchTerm, setSearchTerm] = useState<string>("");
  // State for display properties (after search filtering)
  const [displayProperties, setDisplayProperties] = useState<any[]>([]);
  const [loadingReportId, setLoadingReportId] = useState<string | null>(null);
  // Get property report cost from env settings, default to 15
  const propertyReportCost = parseInt(import.meta.env.VITE_INSIGHTS_COSTS_PROPERTY_REPORT || '15', 10);
  
  // Filter out properties with empty unit numbers when they share the same lat/lng
  useEffect(() => {
    try {
      if (!properties || properties.length === 0) {
        setFilteredProperties([]);
        setLocationGroups({});
        setDisplayProperties([]);
        setError(null);
        return;
      }
      
      // Group properties by lat/lng
      const groups: Record<string, any[]> = {};
      
      properties.forEach(property => {
        const locationKey = `${property.latitude},${property.longitude}`;
        if (!groups[locationKey]) {
          groups[locationKey] = [];
        }
        groups[locationKey].push(property);
      });

      // Store the location groups for later use
      setLocationGroups(groups);

      // Filter properties based on unit numbers
      const filtered = properties.filter(property => {
        const locationKey = `${property.latitude},${property.longitude}`;
        const sameLocationProperties = groups[locationKey];
        
        // If there's only one property at this location, keep it
        if (sameLocationProperties.length === 1) {
          return true;
        }
        
        // If there are multiple properties at the same location,
        // only keep those with unit numbers
        return property.unitNumber && property.unitNumber.trim() !== '';
      });

      setFilteredProperties(filtered);
      setError(null);
    } catch (err) {
      console.error("Error processing properties:", err);
      setError("Error processing properties. Please try again.");
    }
  }, [properties]);

  // Apply search filter whenever filteredProperties or searchTerm changes
  useEffect(() => {
    try {
      if (filteredProperties.length === 0) {
        setDisplayProperties([]);
        return;
      }
      
      if (searchTerm.trim() === '') {
        setDisplayProperties(filteredProperties);
        return;
      }

      const term = searchTerm.toLowerCase().trim();
      const filtered = filteredProperties.filter(property => {
        // Format address for search
        const addressParts = [
          property.streetNumber, 
          property.streetName || '',
          property.streetType || '',
          property.suburb || '',
          property.province || ''
        ].filter(Boolean);
        const address = addressParts.join(' ').toLowerCase();
        
        // Format ERF/Portion for search - ensure it's a string
        let erfDisplay = (property.erf || '').toString();
        if (property.portion && property.portion !== '0') {
          erfDisplay += `/${property.portion}`;
        }
        
        // Format owner name for search - only include if owner details are purchased
        const hasOwnerDetails = property.ownerDetails && property.ownerDetails.length > 0 && property.ownerDetailsGenerationDate;
        const ownerName = hasOwnerDetails && property.buyerName ? property.buyerName.toLowerCase() : '';
        
        // Format unit number for search
        const unitNumber = property.unitNumber ? property.unitNumber.toLowerCase() : '';
        
        // Format property name for search
        const propertyName = property.propertyName ? property.propertyName.toLowerCase() : '';
        
        // Check if any field contains the search term
        return (
          address.includes(term) || 
          erfDisplay.toLowerCase().includes(term) || 
          ownerName.includes(term) || 
          unitNumber.includes(term) ||
          propertyName.includes(term)
        );
      });
      
      setDisplayProperties(filtered);
      setError(null);
    } catch (err) {
      console.error("Error applying search filter:", err);
      setError("Error filtering properties. Please try again.");
    }
  }, [filteredProperties, searchTerm]);
  
  // Toggle expansion state for a property group
  const toggleGroupExpansion = (propertyName: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [propertyName]: !prev[propertyName]
    }));
  };

  // Helper function to convert text to Title Case
  const toTitleCase = (text: string): string => {
    if (!text) return '';
    
    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Define property type colors mapping (same as in MapSearch)
  const propertyTypeColors = {
    "Full Title": "#FF5733", // Orange-red
    "Sectional Title": "#33A1FF", // Blue
    "Farm": "#33FF57", // Green
    "Complex": "#A233FF", // Purple
    "Other": "#FFD700"  // Gold (for any undefined types)
  };

  // Function to get color for a property type
  const getPropertyTypeColor = (propertyType: string) => {
    return propertyTypeColors[propertyType as keyof typeof propertyTypeColors] || propertyTypeColors.Other;
  };

  // Format dates for owner details display
  const formatDateString = (dateString: string | undefined): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    // Format as DD MMM YYYY (e.g., 12 Jun 2023)
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Handler for location marker click
  const handleLocationClick = (property: any) => {
    // First, try to find and click the map tab to ensure it's selected
    const mapTab = document.querySelector('a[href="#mapSearch"]');
    if (mapTab) {
      (mapTab as HTMLElement).click();
    }
    
    // Get all properties at this location
    const locationKey = `${property.latitude},${property.longitude}`;
    const propertiesAtLocation = locationGroups[locationKey] || [property];
    
    // Sort properties by unit number
    const sortedProperties = [...propertiesAtLocation].sort((a, b) => {
      const unitA = a.unitNumber || '';
      const unitB = b.unitNumber || '';
      
      // Try to compare as numbers if both are numeric
      const numA = parseInt(unitA, 10);
      const numB = parseInt(unitB, 10);
      
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      
      // Otherwise compare as strings
      return unitA.localeCompare(unitB);
    });
    
    // If there are multiple properties at this location, pass all of them
    // to allow the map component to show a dropdown
    if (sortedProperties.length > 1) {
      // Then call the onLocationClick handler with all properties at this location
      if (onLocationClick) {
        // Pass the entire array of properties at this location
        onLocationClick({
          ...property,
          multiUnitProperties: sortedProperties
        });
      }
    } else {
      // For single properties, just pass the property
      if (onLocationClick) {
        onLocationClick(property);
      }
    }
  };

  // Handler for owner details button click
  const handleOwnerDetailsClick = (property: any) => {
    // Use the provided onOwnerDetailsClick handler if available
    if (onOwnerDetailsClick) {
      // Check if this is a multi-unit property
      const isMultiUnit = property.unitNumber && property.unitNumber.trim() !== '';
      
      // Add the isMultiUnit flag to the property object
      const enhancedProperty = {
        ...property,
        isMultiUnit: isMultiUnit
      };
      
      // Call the handler with the enhanced property
      onOwnerDetailsClick(enhancedProperty);
    } else {
      // Fallback to the old behavior if no handler is provided
      try {
        // Parse the owner details if it's a string
        let ownerDetailsData;
        if (typeof property.ownerDetails === 'string') {
          ownerDetailsData = JSON.parse(property.ownerDetails);
        } else {
          ownerDetailsData = property.ownerDetails;
        }
        
        // Format address for display
        const addressParts = [
          property.streetNumber, 
          property.streetName ? toTitleCase(property.streetName) : '',
          property.streetType ? toTitleCase(property.streetType) : ''
        ].filter(Boolean);
        
        // Join address parts with spaces
        let streetAddress = addressParts.join(' ');
        
        // Add suburb if available
        if (property.suburb) {
          streetAddress += streetAddress ? `, ${toTitleCase(property.suburb)}` : toTitleCase(property.suburb);
        }
        
        // Check if this is a multi-unit property
        const isMultiUnit = property.unitNumber && property.unitNumber.trim() !== '';
        
        // Dispatch custom event to show owner details directly
        const event = new CustomEvent('showOwnerDetails', {
          detail: {
            propertyId: property.propertyId,
            propertyType: property.propertyType,
            streetAddress: streetAddress,
            ownerDetailsData: ownerDetailsData,
            isMultiUnit: isMultiUnit,
            unitNumber: property.unitNumber || ''
          }
        });
        document.dispatchEvent(event);
      } catch (error) {
        console.error("Error parsing owner details:", error);
        toast.error("Error displaying owner details. Please try again.");
      }
    }
  };

  // Handler for buy owner details button click
  const handleBuyOwnerDetailsClick = (e: React.MouseEvent) => {
    // Prevent default behavior
    e.preventDefault();
    e.stopPropagation();
    
    // Get the property from the clicked button's data attribute
    const propertyStr = e.currentTarget.getAttribute('data-property');
    
    if (!propertyStr) {
      console.error("No property data found");
      toast.error("Error: Property data not found");
      return;
    }
    
    try {
      // Parse the property JSON
      const propertyObj = JSON.parse(propertyStr);
      
      console.log("Buy owner details button clicked for property:", propertyObj.propertyId);
      
      // Format address for display - needed for direct generation
      const addressParts = [
        propertyObj.streetNumber, 
        propertyObj.streetName ? toTitleCase(propertyObj.streetName) : '',
        propertyObj.streetType ? toTitleCase(propertyObj.streetType) : ''
      ].filter(Boolean);
      
      // Join address parts with spaces
      let streetAddress = addressParts.join(' ');
      
      // Add suburb if available
      if (propertyObj.suburb) {
        streetAddress += streetAddress ? `, ${toTitleCase(propertyObj.suburb)}` : toTitleCase(propertyObj.suburb);
      }
      
      // Add the formatted address to the property object
      // Also ensure the unitNumber property is set if it exists
      const enhancedPropertyObj = {
        ...propertyObj,
        formattedAddress: streetAddress,
        // Ensure unitNumber is explicitly set if it exists
        unitNumber: propertyObj.unitNumber || '',
        // Flag this as a multi-unit property if it has a unit number
        isMultiUnit: Boolean(propertyObj.unitNumber && propertyObj.unitNumber.trim() !== '')
      };
      
      console.log("Enhanced property object:", {
        propertyId: enhancedPropertyObj.propertyId,
        unitNumber: enhancedPropertyObj.unitNumber,
        isMultiUnit: enhancedPropertyObj.isMultiUnit
      });
      
      // Use the provided onOwnerDetailsClick handler if available
      if (onOwnerDetailsClick) {
        // Call the handler directly with the enhanced property object
        onOwnerDetailsClick(enhancedPropertyObj);
      } else {
        // Fallback to the old behavior if no handler is provided
        // Dispatch custom event to trigger purchase flow
        const event = new CustomEvent('handleOwnerDetailsClick', {
          detail: { property: enhancedPropertyObj }
        });
        document.dispatchEvent(event);
      }
    } catch (error) {
      console.error("Error parsing property data:", error);
      toast.error("Error processing property data. Please try again.");
    }
  };

  // Handler for property report button click
  const handlePropertyReportClick = (property: any) => {
    if (property.reportId && property.reportGenerationDate) {
      // If onPropertyReportClick is provided, use it
      if (onPropertyReportClick) {
        onPropertyReportClick(property);
        return;
      }
      
      // Otherwise, handle it locally
      setLoadingReportId(property.reportId);
      
      // Show processing message
      toast.info("Downloading property report...");
      
      apiAgent.PropertyReports.downloadReport(property.reportId)
        .then(response => {
          // Create a blob URL and trigger download
          const url = window.URL.createObjectURL(response.data);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `property-report-${property.propertyId}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          
          toast.success("Property report downloaded successfully");
          setLoadingReportId(null);
        })
        .catch(error => {
          console.error('Error downloading property report:', error);
          toast.error("Error downloading property report");
          setLoadingReportId(null);
        });
    }
  };

  // Group properties by property name
  const groupedProperties = displayProperties.reduce((acc, property) => {
    const propertyName = property.propertyName ? toTitleCase(property.propertyName) : '-';
    if (!acc[propertyName]) {
      acc[propertyName] = [];
    }
    acc[propertyName].push(property);
    return acc;
  }, {} as Record<string, any[]>);

  // Sort property names alphabetically
  const sortedPropertyNames = Object.keys(groupedProperties).sort();

  // Sort properties within each group by unit number
  sortedPropertyNames.forEach(propertyName => {
    groupedProperties[propertyName].sort((a: any, b: any) => {
      const unitA = a.unitNumber || '';
      const unitB = b.unitNumber || '';
      
      // Try to compare as numbers if both are numeric
      const numA = parseInt(unitA, 10);
      const numB = parseInt(unitB, 10);
      
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      
      // Otherwise compare as strings
      return unitA.localeCompare(unitB);
    });
  });

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // If there's an error, show it
  if (error) {
    return (
      <div className="property-results mt-4">
        <div className="alert alert-danger">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="property-results mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="search-container" style={{ width: '100%', maxWidth: '650px' }}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Filter these results by address, erf, unit, property name or owner..."
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ 
                border: '1px solid #ced4da',
                padding: '0.375rem 0.75rem',
                fontSize: '12px',
                width: '300px'
              }}
            />
          </div>
          <small className="text-muted mt-1" style={{ display: 'block' }}>
            Showing {displayProperties.length} of {filteredProperties.length} properties
          </small>
        </div>
      </div>
      <div className="table-responsive">
        {displayProperties.length === 0 ? (
          <div className="alert alert-info">
            No properties match your filter criteria. Try adjusting your search.
          </div>
        ) : (
          <table className="table table-hover">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Property Type</th>
                <th style={{ textAlign: 'left' }}>Address</th>
                <th style={{ textAlign: 'left' }}>Property Report</th>
                <th style={{ textAlign: 'left' }}>Unit</th>
                <th style={{ textAlign: 'left' }}>Erf</th>
                <th style={{ textAlign: 'left' }}>Owner</th>
                <th style={{ textAlign: 'left' }}>Size (m²)</th>
              </tr>
            </thead>
            <tbody>
              {sortedPropertyNames.flatMap(propertyName => {
                const propertiesInGroup = groupedProperties[propertyName];
                const propertyCount = propertiesInGroup.length;
                const isExpanded = expandedGroups[propertyName] || false;
                
                // Create an array to hold all rows for this property group
                const rows = [];
                
                // Add the property group header row
                rows.push(
                  <tr 
                    key={`group-${propertyName}`} 
                    onClick={() => toggleGroupExpansion(propertyName)}
                    className="property-group-header"
                    style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                  >
                    <td colSpan={7} style={{ textAlign: 'left', padding: '10px 15px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FontAwesomeIcon 
                          icon={isExpanded ? faChevronDown : faChevronRight} 
                          style={{ width: '14px' }}
                        />
                        <span>{propertyName}</span>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          backgroundColor: '#e9ecef', 
                          borderRadius: '16px', 
                          padding: '2px 8px',
                          fontSize: '12px',
                          color: '#495057',
                          fontWeight: 'normal'
                        }}>
                          <FontAwesomeIcon icon={faBuilding} style={{ marginRight: '4px' }} />
                          <span>{propertyCount}</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
                
                // Add the property rows if the group is expanded
                if (isExpanded) {
                  propertiesInGroup.forEach((property: any, index: number) => {
                    // Format ERF/Portion display
                    let erfDisplay = property.erf || '';
                    if (property.portion && property.portion !== '0') {
                      erfDisplay += `/${property.portion}`;
                    }
                    
                    // Format owner name in Title Case
                    const ownerName = property.buyerName ? toTitleCase(property.buyerName) : '';
                    
                    // Round the extent to 0 decimal places
                    const roundedExtent = Math.round(property.extent);
                    
                    // Format address components in CamelCase
                    const addressParts = [
                      property.streetNumber, 
                      property.streetName ? toTitleCase(property.streetName) : '',
                      property.streetType ? toTitleCase(property.streetType) : ''
                    ].filter(Boolean);
                    
                    // Join address parts with spaces
                    let address = addressParts.join(' ');
                    
                    // Add suburb and province if available
                    if (property.suburb) {
                      address += address ? `, ${toTitleCase(property.suburb)}` : toTitleCase(property.suburb);
                    }
                    
                    if (property.province) {
                      address += address ? `, ${toTitleCase(property.province)}` : toTitleCase(property.province);
                    }
                    
                    const hasOwnerDetails = property.ownerDetails && property.ownerDetails.length > 0 && property.ownerDetailsGenerationDate;
                    
                    // Check if property report is available
                    const hasPropertyReport = property.reportGenerationDate && new Date(property.reportGenerationDate).toString() !== 'Invalid Date';
                    const reportDate = formatDateString(property.reportGenerationDate);
                    
                    rows.push(
                      <tr key={`property-${property.propertyId || index}`} style={{ backgroundColor: 'white' }}>
                        <td style={{ textAlign: 'left', paddingLeft: '35px' }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div
                              style={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                backgroundColor: getPropertyTypeColor(property.propertyType),
                                marginRight: '8px',
                                display: 'inline-block'
                              }}
                            />
                            {property.propertyType}
                          </div>
                        </td>
                        <td style={{ textAlign: 'left' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button 
                              className="btn btn-default btn-sm" 
                              title="Show on map"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click event
                                handleLocationClick(property);
                              }}
                              style={{ 
                                color: 'var(--color2)', 
                                border: '1px solid var(--color2)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                flexShrink: 0
                              }}
                            >
                              <FontAwesomeIcon icon={faMapMarkerAlt} />
                              <span style={{ fontSize: '12px' }}>Map</span>
                              <span className="sr-only">View on map</span>
                            </button>
                            <span>{address || '-'}</span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'left' }}>
                          {hasPropertyReport ? (
                            <button 
                              className="btn btn-default btn-sm" 
                              title="Download property report"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click event
                                handlePropertyReportClick(property);
                              }}
                              disabled={loadingReportId === property.reportId}
                              style={{ 
                                color: 'var(--color2)', 
                                border: '1px solid var(--color2)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                flexShrink: 0
                              }}
                            >
                              <FontAwesomeIcon icon={faFileLines} />
                              <span style={{ fontSize: '12px' }}>
                                {loadingReportId === property.reportId ? 'Downloading...' : `${reportDate} - Report`}
                              </span>
                              <span className="sr-only">Download property report</span>
                            </button>
                          ) : (
                            <button 
                              className="btn btn-default btn-sm" 
                              title="Buy property report"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click event
                                // Use the provided onPropertyReportClick handler if available
                                if (onPropertyReportClick) {
                                  onPropertyReportClick(property);
                                } else {
                                  // Fallback to the old behavior if no handler is provided
                                  // Dispatch custom event to generate property report
                                  const event = new CustomEvent('generatePropertyReport', {
                                    detail: { property }
                                  });
                                  document.dispatchEvent(event);
                                }
                              }}
                              style={{ 
                                color: 'var(--color2)', 
                                border: '1px solid var(--color2)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                flexShrink: 0
                              }}
                            >
                              <FontAwesomeIcon icon={faShoppingCart} />
                              <span style={{ fontSize: '12px' }}>
                                Buy Property Report ({propertyReportCost} Credits)
                              </span>
                              <span className="sr-only">Buy property report</span>
                            </button>
                          )}
                        </td>
                        <td style={{ textAlign: 'left' }}>{property.unitNumber || '-'}</td>
                        <td style={{ textAlign: 'left' }}>{erfDisplay || '-'}</td>
                        <td style={{ textAlign: 'left' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {hasOwnerDetails ? (
                              <>
                                <button 
                                  className="btn btn-default btn-sm" 
                                  title="View owner details"
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent row click event
                                    handleOwnerDetailsClick(property);
                                  }}
                                  style={{ 
                                    color: 'var(--color1)', 
                                    border: '1px solid var(--color1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                    flexShrink: 0
                                  }}
                                >
                                  <FontAwesomeIcon icon={faAddressBook} />
                                  <span style={{ fontSize: '12px' }}>
                                    {formatDateString(property.ownerDetailsGenerationDate)} - Owner Details
                                  </span>
                                  <span className="sr-only">View owner details</span>
                                </button>
                                <span>{ownerName || '-'}</span>
                              </>
                            ) : (
                              <button 
                                className="btn btn-default btn-sm" 
                                title="Purchase owner details"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent row click event
                                  handleBuyOwnerDetailsClick(e);
                                }}
                                data-property={JSON.stringify(property)}
                                style={{ 
                                  color: 'var(--color2)', 
                                  border: '1px solid var(--color2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  flexShrink: 0
                                }}
                              >
                                <FontAwesomeIcon icon={faShoppingCart} />
                                <span style={{ fontSize: '12px' }}>
                                  Buy owner details: 5 credits
                                </span>
                                <span className="sr-only">Purchase owner details</span>
                              </button>
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: 'left' }}>{roundedExtent}</td>
                      </tr>
                    );
                  });
                }
                
                return rows;
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MapSearchResults; 